import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { AnyObject } from 'yup/lib/types'
import { LOCAL_USD_ACCOUNT_NUMBER_MIN_LENGTH } from 'pages/ManageBeneficiaries/AddBeneficiary/components/schemas/fields/usdSpecificFields'
import {
  BeneficiaryDetailsCurrency,
  PaymentMethods,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { isIbanFormat } from 'utils/beneficiaries'
import {
  ALPHANUMERIC_VALIDATION,
  NUMERIC_VALIDATION_PATTERN,
} from 'utils/yupSchemas/createNameSchema'
import { ADDRESS_FIELDS_REQUIRED_TOGETHER } from '../constants'
import { USD_LOCAL_ONLY_PAYMENT_METHODS } from '../utils/constants.fiatInstruments'
import {
  isAddressFieldsRequired,
  isBeneficiaryAddressFieldsRequired,
} from '../utils/fiatInstruments'
import { ContactFiatInstrumentFormData } from './contactFiatInstrumentSchema'

// LOCAL EUR and INTERNATIONAL EUR and GBP
const IBAN_MIN_LENGTH = 11
const IBAN_MAX_LENGTH = 40
const BIC_MIN_LENGTH = 8
const BIC_MAX_LENGTH = 11
const ACCOUNT_NUMBER_MIN_LENGTH = 5

// LOCAL GBP
const GBP_ACCOUNT_NUMBER_EXACT_LENGTH = 8
const GBP_SORT_CODE_EXACT_LENGTH = 6

// USD
const CUBIX_ACCOUNT_NUMBER_EXACT_LENGTH = 4
const ACH_FEDWIRE_ACCOUNT_NUMBER_MIN_LENGTH = 4
const ACH_FEDWIRE_ACCOUNT_NUMBER_MAX_LENGTH = 17
const USD_ROUTING_NUMBER_EXACT_LENGTH = 9
export const DEFAULT_CUBIX_BANK_CODE = 'CUESUS33'

// Account number validation
const validateIban = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  const isIban = isIbanFormat(value)

  if (!value) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.iban'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (isIban) {
    if (value?.length < IBAN_MIN_LENGTH || value?.length > IBAN_MAX_LENGTH) {
      return ctx.createError({
        message: t('formFields.errors.aplhanumericFieldMinLength', {
          field: t('formFields.iban'),
          min: IBAN_MIN_LENGTH,
          max: IBAN_MAX_LENGTH,
        }),
      })
    }

    if (!ALPHANUMERIC_VALIDATION.test(value)) {
      return ctx.createError({
        message: t('ibanFormatError'),
      })
    }
  }

  return true
}

const validateGbpAccountNumber = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  if (!value) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.accountNumber'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (!NUMERIC_VALIDATION_PATTERN.test(value)) {
    return ctx.createError({
      message: t('onlyNumericValues'),
    })
  }

  if (value?.length !== GBP_ACCOUNT_NUMBER_EXACT_LENGTH) {
    return ctx.createError({
      message: t('fieldExactLength', {
        field: t('formFields.accountNumber'),
        amount: GBP_ACCOUNT_NUMBER_EXACT_LENGTH,
      }),
    })
  }

  return true
}

const validateUsdAccountNumber = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  const isCubix = ctx?.parent?.paymentMethod === USDBeneficiaryOptions.CUBIX
  const label = isCubix ? t('formFields.cubixAccountNumber') : t('formFields.accountNumber')

  if (!value) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: label,
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (!NUMERIC_VALIDATION_PATTERN.test(value)) {
    return ctx?.createError({
      message: t('onlyNumericValues'),
    })
  }

  if (isCubix) {
    if (value?.length !== CUBIX_ACCOUNT_NUMBER_EXACT_LENGTH) {
      return ctx?.createError({
        message: t?.('fieldExactLength', {
          field: label,
          amount: CUBIX_ACCOUNT_NUMBER_EXACT_LENGTH,
        }),
      })
    }
    return true
  }

  if (value?.length < ACH_FEDWIRE_ACCOUNT_NUMBER_MIN_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMinLength', {
        field: t('formFields.accountNumber'),
        min: ACH_FEDWIRE_ACCOUNT_NUMBER_MIN_LENGTH,
      }),
    })
  }

  if (value?.length > ACH_FEDWIRE_ACCOUNT_NUMBER_MAX_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMaxLength', {
        field: t('formFields.accountNumber'),
        max: ACH_FEDWIRE_ACCOUNT_NUMBER_MAX_LENGTH,
      }),
    })
  }

  return true
}

const validateSwiftAccountNumber = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  const fields = ctx?.parent as ContactFiatInstrumentFormData
  const { currency } = fields || {}
  const isUSD = currency === BeneficiaryDetailsCurrency.USD
  const minLength = isUSD ? LOCAL_USD_ACCOUNT_NUMBER_MIN_LENGTH : ACCOUNT_NUMBER_MIN_LENGTH
  if (!value) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: t('formFields.accountNumber'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (value.length < minLength) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMinLength', {
        field: t('formFields.accountNumber'),
        min: minLength,
      }),
    })
  }

  if (value?.length > IBAN_MAX_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMaxLength', {
        field: t('formFields.accountNumber'),
        max: IBAN_MAX_LENGTH,
      }),
    })
  }

  if (!ALPHANUMERIC_VALIDATION.test(value)) {
    return ctx.createError({
      message: t('fieldAlphanumeric', {
        field: t('formFields.accountNumber'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

export const accountNumberValidation = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const fields = ctx?.parent as ContactFiatInstrumentFormData
  const { currency, paymentMethods } = fields || {}
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)

  if (isSWIFT) {
    return validateSwiftAccountNumber({ value, ctx, t })
  }

  switch (currency) {
    case BeneficiaryDetailsCurrency.USD:
      return validateUsdAccountNumber({ value, ctx, t })
    case BeneficiaryDetailsCurrency.GBP:
      return validateGbpAccountNumber({ value, ctx, t })
    case BeneficiaryDetailsCurrency.EUR:
    default:
      return validateIban({ value, ctx, t })
  }
}

export const accountNumberConfirmValidation = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const fields = ctx?.parent as ContactFiatInstrumentFormData
  const { currency, paymentMethods } = fields || {}
  const isUsd = currency === BeneficiaryDetailsCurrency.USD
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isAccountNumberConfirmationRequired = isUsd && !isSWIFT

  if (!isAccountNumberConfirmationRequired) {
    return true
  }

  const accountNumber = ctx?.parent?.accountNumber
  const accountNumberConfirm = value

  if (!accountNumberConfirm) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: t('formFields.accountNumber'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (accountNumber && accountNumberConfirm && accountNumber !== accountNumberConfirm) {
    return ctx.createError({
      message: t('usdAccountAccountNumberMismatch'),
    })
  }

  return true
}

// Bank code validation
type AccountNumberValidatorProps = {
  value: string
  ctx: yup.TestContext<AnyObject>
  t: TFunction<'translation', undefined>
}

const validateBic = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  if (!value) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.bic'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (value?.length < BIC_MIN_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMinLength', {
        field: t('formFields.bic'),
        min: BIC_MIN_LENGTH,
      }),
    })
  }

  if (value?.length > BIC_MAX_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMaxLength', {
        field: t('formFields.bic'),
        max: BIC_MAX_LENGTH,
      }),
    })
  }

  if (!ALPHANUMERIC_VALIDATION.test(value)) {
    return ctx.createError({
      message: t('invalidBic'),
    })
  }

  return true
}

// SWIFT code validation - required when both Faster Payment and SWIFT are selected
export const validateSwiftCode = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const fields = ctx?.parent as ContactFiatInstrumentFormData
  const { paymentMethods } = fields || {}
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)

  if (!isSwift) {
    return true
  }

  if (!value) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.swiftCode'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (value?.length < BIC_MIN_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMinLength', {
        field: t('formFields.swiftCode'),
        min: BIC_MIN_LENGTH,
      }),
    })
  }

  if (value?.length > BIC_MAX_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMaxLength', {
        field: t('formFields.swiftCode'),
        max: BIC_MAX_LENGTH,
      }),
    })
  }

  if (!ALPHANUMERIC_VALIDATION.test(value)) {
    return ctx.createError({
      message: t('formFields.errors.invalidSwiftCode'),
    })
  }

  return true
}

const validateSortCode = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  if (!value) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.sortCode'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (!NUMERIC_VALIDATION_PATTERN.test(value)) {
    return ctx.createError({
      message: t('onlyNumericValues'),
    })
  }

  if (value?.length !== GBP_SORT_CODE_EXACT_LENGTH) {
    return ctx.createError({
      message: t('fieldExactLength', {
        field: t('formFields.sortCode'),
        amount: GBP_SORT_CODE_EXACT_LENGTH,
      }),
    })
  }

  return true
}

export const bankCodeValidation = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const fields = ctx?.parent as ContactFiatInstrumentFormData
  const { currency, paymentMethods, bankCountryCode } = fields || {}
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isSWIFTOnly = isSWIFT && paymentMethods?.length === 1
  const isFasterPayment = paymentMethods?.includes(PaymentMethods.FASTER_PAYMENT)
  const isGBP = currency === BeneficiaryDetailsCurrency.GBP

  // GBP with both FPS and SWIFT: bankCode is sort code (6 digits), swiftCode is BIC
  if (isGBP && isSWIFT && isFasterPayment) {
    return validateSortCode({ value, ctx, t })
  }

  if (isSWIFTOnly) {
    return true
    // return validateBic({ value, ctx, t })
  }

  switch (currency) {
    case BeneficiaryDetailsCurrency.USD: {
      const isLocalUsPaymentMethodOnly =
        paymentMethods?.some(paymentMethod =>
          USD_LOCAL_ONLY_PAYMENT_METHODS.includes(paymentMethod)
        ) && !isSWIFT
      const isBankCountryUs = bankCountryCode === 'US'
      const shouldValidateRoutingNumber = isLocalUsPaymentMethodOnly || (isSWIFT && isBankCountryUs)

      return shouldValidateRoutingNumber ? validateUsdRoutingNumber({ value, ctx, t }) : true
    }
    case BeneficiaryDetailsCurrency.GBP: {
      const shouldValidateSortCode = (isSWIFT && isFasterPayment) || !isSWIFT
      return shouldValidateSortCode ? validateSortCode({ value, ctx, t }) : true
    }
    case BeneficiaryDetailsCurrency.EUR:
    default:
      return validateBic({ value, ctx, t })
  }
}

// Routing number validation (USD)
export const validateUsdRoutingNumber = ({ value, ctx, t }: AccountNumberValidatorProps) => {
  const { currency, paymentMethods, bankCountryCode } =
    ctx?.parent || ({} as ContactFiatInstrumentFormData)
  const isUSD = currency === BeneficiaryDetailsCurrency.USD
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isBankCountryUs = bankCountryCode === 'US'
  const isLocalUsPaymentMethodOnly =
    paymentMethods?.some(paymentMethod => USD_LOCAL_ONLY_PAYMENT_METHODS.includes(paymentMethod)) &&
    !isSwift

  const isRoutingNumberRequired =
    isUSD && ((isSwift && isBankCountryUs) || isLocalUsPaymentMethodOnly)

  if (!isRoutingNumberRequired) {
    return true
  }

  if (!value) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: t('formFields.routingNumber'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (!NUMERIC_VALIDATION_PATTERN.test(value)) {
    return ctx?.createError({
      message: t('onlyNumericValues'),
    })
  }

  if (value?.length !== USD_ROUTING_NUMBER_EXACT_LENGTH) {
    return ctx.createError({
      message: t('fieldExactLength', {
        field: t('formFields.routingNumber'),
        amount: USD_ROUTING_NUMBER_EXACT_LENGTH,
      }),
    })
  }

  return true
}

export const validateAccountType = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const fields = ctx?.parent as ContactFiatInstrumentFormData
  const { currency, paymentMethods } = fields || {}
  const isUsd = currency === BeneficiaryDetailsCurrency.USD
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isAccountTypeRequired = isUsd && !isSWIFT

  if (!isAccountTypeRequired) {
    return true
  }

  if (!value) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.accountType'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

export const validateBeneficiaryAddressField = (
  value: string,
  ctx: yup.TestContext<ContactFiatInstrumentFormData>,
  t: TFunction<'translation', undefined>,
  label: string
) => {
  const isRequired = isBeneficiaryAddressFieldsRequired({
    field: ctx?.path,
    formFields: ctx?.parent,
  })

  if (!value && isRequired) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: label,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

export const validateBankAddressField = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>,
  label: string
) => {
  const isRequired = isAddressFieldsRequired({
    currency: ctx?.parent?.currency,
    isSwiftPaymentMethodSelected:
      ctx?.parent?.paymentMethods?.includes(PaymentMethods.SWIFT) ?? false,
  })

  if (!value && isRequired) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: label,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

export const validateBankName = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>,
  label: string
) => {
  const { currency, paymentMethods } = ctx?.parent as ContactFiatInstrumentFormData
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isZar = currency === BeneficiaryDetailsCurrency.ZAR && !isSwift
  const isRequired = isSwift || isZar

  if (!isRequired) {
    return true
  }

  if (!value) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: label,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

// intermediary bank validation
export const validateIntermediaryBankName = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>,
  label: string
) => {
  const isRequired = ctx?.parent?.isIntermediaryBankDetailsRequired

  if (!value && isRequired) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: label,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

export const validateIntermediarySwiftCode = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const isRequired = ctx?.parent?.isIntermediaryBankDetailsRequired

  if (!value && isRequired) {
    return ctx.createError({
      message: t('fieldIsARequiredField', {
        field: t('formFields.swiftCode'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (value?.length < BIC_MIN_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMinLength', {
        field: t('formFields.swiftCode'),
        min: BIC_MIN_LENGTH,
      }),
    })
  }

  if (value?.length > BIC_MAX_LENGTH) {
    return ctx.createError({
      message: t('formFields.errors.aplhanumericFieldMaxLength', {
        field: t('formFields.swiftCode'),
        max: BIC_MAX_LENGTH,
      }),
    })
  }

  if (!ALPHANUMERIC_VALIDATION.test(value)) {
    return ctx.createError({
      message: t('formFields.errors.invalidSwiftCode'),
    })
  }

  return true
}

export const validateIntermediaryBankCode = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>
) => {
  const isRequired =
    ctx?.parent?.isIntermediaryBankDetailsRequired &&
    ctx?.parent?.currency === BeneficiaryDetailsCurrency.USD &&
    ctx?.parent?.paymentMethods?.includes(PaymentMethods.SWIFT) &&
    ctx?.parent?.intermediaryBankCountryCode === 'US'

  if (!value && isRequired) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: t?.('formFields.routingNumber'),
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

// Intermediary bank address validation
export const validateIntermediaryAddressField = (
  value: string,
  ctx: yup.TestContext<AnyObject>,
  t: TFunction<'translation', undefined>,
  label: string
) => {
  const otherFields = ADDRESS_FIELDS_REQUIRED_TOGETHER?.filter(field => field !== ctx.path)
  const isRequired =
    otherFields?.some(addressField => !!ctx?.parent?.[addressField]) ||
    ctx?.parent?.isIntermediaryBankDetailsRequired

  if (!value && isRequired) {
    return ctx.createError({
      message: t?.('fieldIsARequiredField', {
        field: label,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}
